/**
 * Serializable schemas for Suggestion domain types (public-facing).
 */

import { z } from "zod";
import { EventParticipantIdPublicSchema } from "./branded-id.schemas.js";
import { SuggestionTypeSchema } from "./suggestion-type.schemas.js";
import { LocalizedTextSchema } from "./localized-text.schemas.js";
import { paginatedResponseSchema } from "./paginated.schemas.js";

/**
 * Schema for Suggestion (shared, public refs). `.parse(json)` -> `Suggestion`.
 */
export const SuggestionSchema = z.object({
  targetParticipantIdPublic: EventParticipantIdPublicSchema,
  suggestionType: SuggestionTypeSchema,
  /** 1-based position in the reader's deck; the only ordering signal a suggestion carries. */
  rank: z.number(),

  whyConnect: LocalizedTextSchema.optional(),

  /**
   * Present, and only ever `true`, on a suggestion that came from an override. The
   * reader's endpoint drops it from every row unless the event turned the badge on, so
   * absence is the only shape for "not overridden".
   */
  isOverride: z.literal(true).optional(),
});

/**
 * Suggestion — public-facing type derived from the shared schema.
 * Frontend and API consumers use this type.
 */
export type Suggestion = z.infer<typeof SuggestionSchema>;

/**
 * A page of suggestions plus the one fact that is about the page rather than any row
 * in it: whether a matching run that will cover this reader is queued or
 * executing. It rides on the list because the reader needs both together — a
 * deck is read differently when a new one is on its way — and the list response
 * was already an object, so this is a field rather than a new envelope.
 */
export const SuggestionsPageSchema = paginatedResponseSchema(
  SuggestionSchema,
).extend({
  recalculationPending: z.boolean(),
});

export type SuggestionsPage = z.infer<typeof SuggestionsPageSchema>;
